import { css, Global } from '@emotion/react';
import styled from '@emotion/styled';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { PaymentRequestEvent } from '@stripe/stripe-js';
import { ChangeEvent, FormEvent, ReactNode, useCallback, useState } from 'react';
import { microcopySet, useMicrocopy } from '../../../../utils/contentfulMicrocopy';
import { spacing } from '../../../../utils/styleguide';
import TextInput, { runValidations, validations } from '../../forms/TextInput';
import Icon from '../../Icon';
import Typography from '../../text/Typography';

const StripeAddCardWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
`;

const GlobalStyles = css`
  .stripe-add-card .StripeElement {
    height: 56px;
    border: 1px solid #acb5bd;
    padding: 0 16px;
  }
  .stripe-add-card .StripeElement--invalid {
    border-color: #fa755a;
  }
  .stripe-add-card .StripeElement--webkit-autofill {
    background-color: #fefde5 !important;
  }
`;

const StripeAddCardForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${spacing[3]}px;
`;

const StripeCardElementWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardElementOptions = {
  style: {
    base: {
      lineHeight: '56px',
      fontSize: '16px',
      fontFamily: 'Sharp-Grotesk, Helvetica, Arial, sans-serif',
    },
  },
};

type StripePaymentMethodFormProps = {
  onPaymentMethod: (
    paymentMethod: string,
    type: PaymentRequestEvent['walletName'],
    obj: { email: string; firstName?: string; lastName?: string },
  ) => void;
  variant?: 'verbose' | 'dense';
  buttonLabel?: string;
  children: ReactNode;
  onError: (errorMessage: string) => void;
  onLoading: (loading: boolean) => void;
};

export default function StripePaymentMethodForm({
  onPaymentMethod,
  variant,
  children,
  onError,
  onLoading,
}: StripePaymentMethodFormProps) {
  const t = useMicrocopy();
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const elements = useElements();
  const stripe = useStripe();
  const emailLabel = t(microcopySet.PAYMENT_OPTION, 'checkoutEmail');

  const validateInput = async () => {
    if (!stripe || !elements) {
      return false;
    }
    const emailValidationResponse = runValidations(validations.email, email);
    const lastNameValidationResponse = runValidations(validations.familyName, lastName);
    const firstNameValidationResponse =
      variant === runValidations(validations.givenName, firstName);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return false;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });

    if (
      !lastNameValidationResponse &&
      !emailValidationResponse &&
      !firstNameValidationResponse &&
      !error
    ) {
      return paymentMethod;
    }

    if (error) {
      onError(error.message as string);
    }

    setShowAllErrors(true);
    onLoading(false);

    return false;
  };

  const handleAddCard = async (event?: FormEvent) => {
    event?.preventDefault();
    onLoading(true);
    clearErrors();

    const paymentMethod = await validateInput();

    onLoading(false);
    if (paymentMethod) {
      onPaymentMethod(paymentMethod.id, 'card', {
        email,
        firstName: firstName.length > 0 ? firstName : undefined,
        lastName: lastName.length ? lastName : undefined,
      });
    }
  };

  const clearErrors = () => {
    onError('');
  };

  const onFirstNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setFirstName(event.target.value),
    [setFirstName],
  );
  const onLastNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setLastName(event.target.value),
    [setLastName],
  );
  const onEmailChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value),
    [setEmail],
  );

  return (
    <StripeAddCardWrapper className="stripe-add-card">
      <Global styles={GlobalStyles} />
      <StripeAddCardForm onSubmit={handleAddCard}>
        {variant === 'verbose' && (
          <Typography
            useBuiltInMargins
            variant="h3"
            css={css`
              margin: ${spacing[4]}px 0px;
            `}
          >
            Payment details
          </Typography>
        )}

        <TextInput
          value={firstName}
          label="First name"
          onChange={onFirstNameChange}
          validations={validations.givenName}
          validateWithoutFocus={showAllErrors}
        />
        <TextInput
          value={lastName}
          label="Last name"
          onChange={onLastNameChange}
          validations={validations.familyName}
          validateWithoutFocus={showAllErrors}
        />
        <TextInput
          value={email}
          label={emailLabel}
          onChange={onEmailChange}
          validations={validations.email}
          validateWithoutFocus={showAllErrors}
        />

        <StripeCardElementWrapper>
          {/** Stripe CardElement lives in its own iframe,
           * which means it doesn't integrate in the form as well as regular html input
           * In particular the form is submitted when the user presses 'return' in the last regular input
           * The form should also be submitted when the user presses 'return' in the CardElement
           * */}
          <CardElement options={CardElementOptions} />
          <Icon
            name="stripe-icon"
            css={css`
              align-self: flex-end;
              height: 18px;
              width: 80px;
              margin: 6px 0 10px;
            `}
          />
        </StripeCardElementWrapper>

        {children}
      </StripeAddCardForm>
    </StripeAddCardWrapper>
  );
}
